import * as React from "react";
import Header from "./Header";
import PaperList from "./PaperList";
import Login from "./Login";
import InlineCitationPanel from "./InlineCitationPanel";
import DiagnosticsPanel from "./DiagnosticsPanel";
import { 
  makeStyles, 
  MessageBar, 
  MessageBarBody, 
  MessageBarTitle, 
  Button,
  Dropdown,
  Option,
  Label,
  Input,
  Spinner,
  Text
} from "@fluentui/react-components";
import { BookSearch24Regular, ArrowClockwise24Regular, SignOut24Regular, Search24Regular, Person24Filled, ChevronDown24Regular, ChevronUp24Regular } from "@fluentui/react-icons";
import { Paper, CitationStyle } from "../../types/paper";
import { mockPapers } from "../../data/mockPapers";
import { insertCitation } from "../../services/word";
import { generateBibliography, setPapersCache } from "../taskpane";
import { scholarSyncAPI } from "../../services/api";
import { detectInlineCitationTrigger, insertCitationAtCursor } from "../../services/inlineCitation";
import { formatInTextCitation } from "../../services/citation";

interface AppProps {
  title: string;
}

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    willChange: "auto",
  },
  messageContainer: {
    padding: "12px 16px",
    transition: "opacity 0.2s ease",
  },
  topBar: {
    padding: "14px 18px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e3e8ef",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
  },
  userEmail: {
    fontSize: "13px",
    color: "#4a5568",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  buttonGroup: {
    display: "flex",
    gap: "6px",
  },
  iconButton: {
    borderRadius: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f0f4f8",
      transform: "translateY(-1px)",
    },
  },
  section: {
    padding: "18px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e3e8ef",
    transition: "all 0.2s ease",
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#2d3748",
    marginBottom: "10px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.8px",
  },
  searchSection: {
    padding: "18px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e3e8ef",
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  searchLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchCount: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#667eea",
    backgroundColor: "#eef2ff",
    padding: "3px 10px",
    borderRadius: "12px",
  },
  searchInputWrapper: {
    position: "relative",
  },
  collapsibleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "12px 18px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e3e8ef",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  collapsibleTitle: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#2d3748",
    textTransform: "uppercase" as const,
    letterSpacing: "0.8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  collapsibleContent: {
    padding: "18px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e3e8ef",
    animation: "slideDown 0.3s ease",
  },
  dropdownWrapper: {
    width: "100%",
  },
  previewSection: {
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    border: "1px solid #e3e8ef",
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    marginBottom: "8px",
  },
  previewTitle: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  previewContent: {
    marginTop: "12px",
    padding: "12px",
    backgroundColor: "#ffffff",
    borderRadius: "6px",
    border: "1px solid #e3e8ef",
  },
  previewLabel: {
    fontSize: "10px",
    fontWeight: "600",
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  previewInText: {
    fontSize: "13px",
    color: "#334155",
    marginBottom: "12px",
    fontStyle: "italic",
  },
  previewBibliography: {
    fontSize: "12px",
    color: "#475569",
    lineHeight: "1.6",
    paddingLeft: "16px",
    textIndent: "-16px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "16px",
    backgroundColor: "#ffffff",
  },
});

const App: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();
  const [papers, setPapers] = React.useState<Paper[]>([]);
  const [filteredPapers, setFilteredPapers] = React.useState<Paper[]>([]);
  const [message, setMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [citationStyle, setCitationStyle] = React.useState<CitationStyle>("APA");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [useMockData, setUseMockData] = React.useState(false);
  const [readingLists, setReadingLists] = React.useState<string[]>([]);
  const [selectedReadingList, setSelectedReadingList] = React.useState<string>("all");
  const [sortOption, setSortOption] = React.useState<string>("relevance");
  const [isCitationStyleOpen, setIsCitationStyleOpen] = React.useState(false);
  const [isReadingListOpen, setIsReadingListOpen] = React.useState(false);
  const [isPapersOpen, setIsPapersOpen] = React.useState(true);
  const [isBibliographyOpen, setIsBibliographyOpen] = React.useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const [isStylePreviewOpen, setIsStylePreviewOpen] = React.useState(false);
  
  // Inline citation state
  const [inlineCitationActive, setInlineCitationActive] = React.useState(false);
  const [inlineSearchText, setInlineSearchText] = React.useState("");
  const [inlineFilteredPapers, setInlineFilteredPapers] = React.useState<Paper[]>([]);
  const [inlineSelectedIndex, setInlineSelectedIndex] = React.useState(0);
  const inlineCheckIntervalRef = React.useRef<number | null>(null);
  const isInsertingCitationRef = React.useRef<boolean>(false); // Track if citation is being inserted
  
  // Multi-select state
  const [selectedPaperIds, setSelectedPaperIds] = React.useState<Set<string>>(new Set());
  
  // Diagnostics state
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = React.useState(false);

  // Check if user is already logged in on mount
  React.useEffect(() => {
    const userEmail = scholarSyncAPI.getUserEmail();
    if (userEmail) {
      setIsLoggedIn(true);
      loadReadingLists();
      loadPapers();
    }
  }, []);

  // Start inline citation detection when logged in
  React.useEffect(() => {
    if (!isLoggedIn) return undefined;

    let isChecking = false; // Prevent overlapping checks

    // Check for '@' trigger every 750ms (increased from 500ms for better performance)
    const checkForTrigger = async () => {
      if (isChecking || isInsertingCitationRef.current) return; // Skip if previous check still running OR citation being inserted
      isChecking = true;

      try {
        const trigger = await detectInlineCitationTrigger();
        if (trigger.detected) {
          // Always update if detected - this allows search text to update as user types
          setInlineCitationActive(true);
          setInlineSearchText(trigger.searchText);
          if (!inlineCitationActive) {
            // Only reset index when first activating
            setInlineSelectedIndex(0);
          }
        } else if (inlineCitationActive) {
          // If it was active but no longer detected, close it
          setInlineCitationActive(false);
          setInlineSearchText("");
        }
      } catch (error) {
        // Silently fail - user might not be in Word document
      } finally {
        isChecking = false;
      }
    };

    inlineCheckIntervalRef.current = window.setInterval(checkForTrigger, 750);

    return () => {
      if (inlineCheckIntervalRef.current) {
        clearInterval(inlineCheckIntervalRef.current);
      }
    };
  }, [isLoggedIn, inlineCitationActive]);

  // Filter and sort papers when search query or sort option changes
  React.useEffect(() => {
    let results = papers;
    
    // Apply search filter
    if (searchQuery.trim() !== "") {
      results = scholarSyncAPI.searchPapers(papers, searchQuery);
    }
    
    // Apply sorting
    results = sortPapers(results, sortOption);
    
    setFilteredPapers(results);
  }, [searchQuery, papers, sortOption]);

  // Filter papers for inline citation based on inline search text
  React.useEffect(() => {
    if (inlineSearchText.trim() === "") {
      setInlineFilteredPapers(papers.slice(0, 10)); // Show first 10 papers
    } else {
      const searchLower = inlineSearchText.toLowerCase().trim();
      let filtered: Paper[] = [];

      // Special keyword searches
      if (searchLower === "starred" || searchLower === "important" || searchLower === "star") {
        // Show starred papers only
        filtered = papers.filter(p => p.starred);
      } else if (/^\d{4}$/.test(searchLower)) {
        // Year search (e.g., @2024)
        const year = parseInt(searchLower);
        filtered = papers.filter(p => p.year === year);
      } else if (/^\d{4}-\d{4}$/.test(searchLower)) {
        // Year range search (e.g., @2020-2024)
        const [startYear, endYear] = searchLower.split("-").map(y => parseInt(y));
        filtered = papers.filter(p => p.year >= startYear && p.year <= endYear);
      } else if (searchLower.startsWith("recent")) {
        // Recent papers (last 2 years)
        const currentYear = new Date().getFullYear();
        filtered = papers.filter(p => p.year >= currentYear - 2);
      } else if (searchLower.startsWith("rating:") || searchLower.startsWith("rated:")) {
        // Rating filter (e.g., @rating:4 or @rated:4+)
        const ratingMatch = searchLower.match(/(?:rating|rated):(\d)(\+?)/);
        if (ratingMatch) {
          const minRating = parseInt(ratingMatch[1]);
          const orHigher = ratingMatch[2] === "+";
          filtered = papers.filter(p => {
            const ratings = [p.clarity, p.usefulness, p.impact].filter(r => r !== undefined);
            if (ratings.length === 0) return false;
            const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
            return orHigher ? avg >= minRating : Math.round(avg) === minRating;
          });
        }
      } else {
        // Regular search (title, author, keywords, journal)
        filtered = scholarSyncAPI.searchPapers(papers, searchLower);
      }

      setInlineFilteredPapers(filtered.slice(0, 10)); // Limit to 10 results
    }
    setInlineSelectedIndex(0); // Reset selection when search changes
  }, [inlineSearchText, papers]);

  const handleInlineCitationSelect = async (paper: Paper) => {
    try {
      // Pause polling during insertion
      isInsertingCitationRef.current = true;
      
      const citation = formatInTextCitation(paper, citationStyle);
      await insertCitationAtCursor(citation, paper.id);
      
      setMessage({ 
        type: "success", 
        text: `✅ Inline citation inserted: ${paper.authors[0].split(',')[0]} (${paper.year})` 
      });
      setTimeout(() => setMessage(null), 3000);
      
      // Close inline panel
      setInlineCitationActive(false);
      setInlineSearchText("");
      
      // Wait a moment for Word to stabilize before resuming polling
      setTimeout(() => {
        isInsertingCitationRef.current = false;
      }, 500);
    } catch (error) {
      isInsertingCitationRef.current = false; // Resume polling even on error
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessage({ type: "error", text: `❌ Failed to insert citation: ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleTogglePaperSelection = React.useCallback((paperId: string) => {
    setSelectedPaperIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(paperId)) {
        newSet.delete(paperId);
      } else {
        newSet.add(paperId);
      }
      return newSet;
    });
  }, []);

  const handleClearSelection = React.useCallback(() => {
    setSelectedPaperIds(new Set());
  }, []);

  const handleInsertMultipleCitations = React.useCallback(async () => {
    if (selectedPaperIds.size === 0) return;

    try {
      // Get selected papers in order they appear in the list
      const selectedPapers = filteredPapers.filter(p => selectedPaperIds.has(p.id));
      
      // Sort by year (oldest first) for citation convention
      const sortedPapers = [...selectedPapers].sort((a, b) => a.year - b.year);
      
      // Format multiple citations: (Smith et al., 2020; Jones, 2021; Lee, 2022)
      const citations = sortedPapers.map(p => formatInTextCitation(p, citationStyle));
      const combinedCitation = `(${citations.map(c => c.replace(/[()]/g, '')).join('; ')})`;
      
      // Insert as a single content control
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const contentControl = selection.insertContentControl();
        contentControl.appearance = Word.ContentControlAppearance.boundingBox;
        contentControl.title = sortedPapers.map(p => p.id).join(',');
        contentControl.tag = "scholarsync-citation-multiple";
        contentControl.insertText(combinedCitation, Word.InsertLocation.replace);
        contentControl.cannotDelete = false;
        contentControl.cannotEdit = false;
        
        await context.sync();
      });

      setMessage({ 
        type: "success", 
        text: `✅ ${selectedPaperIds.size} citations inserted` 
      });
      setTimeout(() => setMessage(null), 3000);
      
      // Clear selection
      handleClearSelection();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessage({ type: "error", text: `❌ Failed to insert citations: ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  }, [selectedPaperIds, filteredPapers, citationStyle, handleClearSelection]);


  // Keyboard navigation for inline citation
  React.useEffect(() => {
    if (!inlineCitationActive) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setInlineSelectedIndex((prev) => 
          Math.min(prev + 1, inlineFilteredPapers.length - 1)
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setInlineSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (inlineFilteredPapers[inlineSelectedIndex]) {
          handleInlineCitationSelect(inlineFilteredPapers[inlineSelectedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setInlineCitationActive(false);
        setInlineSearchText("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inlineCitationActive, inlineFilteredPapers, inlineSelectedIndex]);

  const sortPapers = (papersToSort: Paper[], option: string): Paper[] => {
    const sorted = [...papersToSort];
    
    switch (option) {
      case "starred":
        // Starred papers first, then by title
        return sorted.sort((a, b) => {
          if (a.starred && !b.starred) return -1;
          if (!a.starred && b.starred) return 1;
          return a.title.localeCompare(b.title);
        });
      
      case "date-accessed-newest":
        return sorted.sort((a, b) => {
          if (!a.dateAccessed) return 1;
          if (!b.dateAccessed) return -1;
          return new Date(b.dateAccessed).getTime() - new Date(a.dateAccessed).getTime();
        });
      
      case "date-accessed-oldest":
        return sorted.sort((a, b) => {
          if (!a.dateAccessed) return 1;
          if (!b.dateAccessed) return -1;
          return new Date(a.dateAccessed).getTime() - new Date(b.dateAccessed).getTime();
        });
      
      case "date-published-newest":
        return sorted.sort((a, b) => b.year - a.year);
      
      case "date-published-oldest":
        return sorted.sort((a, b) => a.year - b.year);
      
      case "relevance":
      default:
        // Default: by title
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
    }
  };

  const getSortDisplayLabel = (option: string): string => {
    switch (option) {
      case "relevance": return "Relevance";
      case "starred": return "Starred Papers";
      case "date-accessed-newest": return "Date Accessed (Newest First)";
      case "date-accessed-oldest": return "Date Accessed (Oldest First)";
      case "date-published-newest": return "Date Published (Newest First)";
      case "date-published-oldest": return "Date Published (Oldest First)";
      default: return "Relevance";
    }
  };

  const getReadingListDisplayLabel = (value: string): string => {
    if (value === "all") return "All Papers";
    return value;
  };

  const loadReadingLists = async () => {
    try {
      const lists = await scholarSyncAPI.getReadingLists();
      setReadingLists(lists);
    } catch (error) {
      console.error("Error loading reading lists:", error);
    }
  };

  const loadPapers = async (readingList?: string) => {
    setIsLoading(true);
    try {
      const fetchedPapers = await scholarSyncAPI.getPapers(readingList && readingList !== "all" ? readingList : undefined);
      setPapers(fetchedPapers);
      setFilteredPapers(fetchedPapers);
      setPapersCache(fetchedPapers); // Update papers cache for bibliography generation
      setMessage({ type: "success", text: `✅ Loaded ${fetchedPapers.length} papers` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("Error loading papers:", error);
      setMessage({ 
        type: "error", 
        text: `❌ Failed to load papers. Using mock data.` 
      });
      // Fallback to mock data
      setPapers(mockPapers);
      setFilteredPapers(mockPapers);
      setPapersCache(mockPapers); // Update papers cache with mock data
      setUseMockData(true);
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReadingListChange = (listName: string) => {
    setSelectedReadingList(listName);
    loadPapers(listName);
  };

  const handleLogin = async (email: string, pin: string): Promise<boolean> => {
    const success = await scholarSyncAPI.login(email, pin);
    if (success) {
      setIsLoggedIn(true);
      await loadReadingLists();
      await loadPapers();
    }
    return success;
  };

  const handleLogout = () => {
    scholarSyncAPI.clearUserEmail();
    setIsLoggedIn(false);
    setPapers([]);
    setFilteredPapers([]);
    setSearchQuery("");
  };

  const handleInsertCitation = async (paper: Paper) => {
    try {
      await insertCitation(paper, citationStyle);
      setMessage({ type: "success", text: `✅ Citation inserted: ${paper.authors[0].split(',')[0]} (${paper.year})` });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      
      // If Word API isn't available, show what the citation would be
      if (errorMsg.includes("Word API is not available")) {
        const { formatInTextCitation } = await import("../../services/citation");
        const citation = formatInTextCitation(paper, citationStyle);
        setMessage({ 
          type: "error", 
          text: `⚠️ Word API not loaded. Citation would be: ${citation}` 
        });
      } else {
        setMessage({ type: "error", text: `❌ Failed to insert citation: ${errorMsg}` });
      }
      
      // Clear message after 5 seconds for errors
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleGenerateBibliography = async () => {
    setIsGenerating(true);
    try {
      await generateBibliography(citationStyle);
      setMessage({ type: "success", text: `✅ Bibliography generated successfully!` });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setMessage({ type: "error", text: `❌ Failed to generate bibliography: ${errorMsg}` });
      
      // Clear message after 5 seconds for errors
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setIsGenerating(false);
    }
  };

  // Show login screen if not logged in
  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className={styles.root}>
      <Header 
        logo="/assets/logo-filled.png" 
        title={props.title} 
        message="Cite as you write"
        onDiagnosticsClick={() => setIsDiagnosticsOpen(true)}
      />

      <div className={styles.topBar}>
        <span className={styles.userEmail}>
          <Person24Filled style={{ color: "#667eea" }} />
          {useMockData ? "Mock Data" : scholarSyncAPI.getUserEmail()}
        </span>
        <div className={styles.buttonGroup}>
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowClockwise24Regular />}
            onClick={() => loadPapers(selectedReadingList)}
            disabled={isLoading}
            className={styles.iconButton}
            title="Refresh papers"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<SignOut24Regular />}
            onClick={handleLogout}
            className={styles.iconButton}
            title="Logout"
          />
        </div>
      </div>

      {message && (
        <div className={styles.messageContainer}>
          <MessageBar intent={message.type === "success" ? "success" : "error"}>
            <MessageBarBody>
              <MessageBarTitle>{message.text}</MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div>
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setIsCitationStyleOpen(!isCitationStyleOpen)}
        >
          <div className={styles.collapsibleTitle}>
            Citation Style
            <span style={{ 
              fontSize: "12px", 
              fontWeight: "600", 
              color: "#667eea",
              backgroundColor: "#eef2ff",
              padding: "2px 8px",
              borderRadius: "6px",
              textTransform: "none",
              letterSpacing: "0",
            }}>
              {citationStyle}
            </span>
          </div>
          {isCitationStyleOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
        </div>
        {isCitationStyleOpen && (
          <div className={styles.collapsibleContent}>
            <div className={styles.dropdownWrapper}>
              <Dropdown
                appearance="outline"
                value={citationStyle}
                selectedOptions={[citationStyle]}
                onOptionSelect={(_, data) => setCitationStyle(data.optionValue as CitationStyle)}
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  border: "2px solid #e3e8ef",
                }}
              >
                <Option value="APA">APA (7th Edition)</Option>
                <Option value="MLA">MLA (9th Edition)</Option>
                <Option value="Chicago">Chicago (17th Edition)</Option>
                <Option value="Harvard">Harvard (Cite Them Right 12th)</Option>
                <Option value="IEEE">IEEE</Option>
                <Option value="UCL-IOE">UCL Institute of Education - Harvard</Option>
              </Dropdown>
            </div>
            
            <div className={styles.previewSection}>
              <div 
                className={styles.previewHeader}
                onClick={() => setIsStylePreviewOpen(!isStylePreviewOpen)}
              >
                <span className={styles.previewTitle}>Preview Citation Style</span>
                {isStylePreviewOpen ? <ChevronUp24Regular style={{ fontSize: "16px", color: "#64748b" }} /> : <ChevronDown24Regular style={{ fontSize: "16px", color: "#64748b" }} />}
              </div>
              
              {isStylePreviewOpen && (
                <div className={styles.previewContent}>
                  {citationStyle === "APA" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>(Smith et al., 2021)</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        Smith, J., Petrovic, P., Rose, M., De Souza, C., Muller, L., Nowak, B., & Martinez, J. (2021). Placeholder text: A study. <i>The Journal of Citation Styles, 3</i>. https://doi.org/10.1010/X
                      </div>
                    </>
                  )}
                  
                  {citationStyle === "MLA" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>(Smith et al.)</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        Smith, John, et al. "Placeholder Text: A Study." <i>The Journal of Citation Styles</i>, vol. 3, 2021, doi:10.1010/X.
                      </div>
                    </>
                  )}
                  
                  {citationStyle === "Chicago" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>(Smith et al. 2021)</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        Smith, John, Peter Petrovic, Mary Rose, Carlos De Souza, Lars Muller, Barbara Nowak, and Juan Martinez. 2021. "Placeholder Text: A Study." <i>The Journal of Citation Styles</i> 3. https://doi.org/10.1010/X.
                      </div>
                    </>
                  )}
                  
                  {citationStyle === "Harvard" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>(Smith et al., 2021)</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        Smith, J., Petrovic, P., Rose, M., De Souza, C., Muller, L., Nowak, B. and Martinez, J. (2021) 'Placeholder text: A study', <i>The Journal of Citation Styles</i>, 3. Available at: https://doi.org/10.1010/X.
                      </div>
                    </>
                  )}
                  
                  {citationStyle === "IEEE" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>[1]</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        [1] J. Smith, P. Petrovic, M. Rose, C. De Souza, L. Muller, B. Nowak, and J. Martinez, "Placeholder text: A study," <i>J. Citation Styles</i>, vol. 3, 2021, doi: 10.1010/X.
                      </div>
                    </>
                  )}
                  
                  {citationStyle === "UCL-IOE" && (
                    <>
                      <div className={styles.previewLabel}>In-Text Citation</div>
                      <div className={styles.previewInText}>(Smith et al., 2021)</div>
                      <div className={styles.previewLabel}>Bibliography Entry</div>
                      <div className={styles.previewBibliography}>
                        Smith, J., Petrovic, P., Rose, M., De Souza, C., Muller, L., Nowak, B. and Martinez, J. (2021). 'Placeholder text: A study'. <i>The Journal of Citation Styles</i>, 3(1), pp.10-20.
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setIsBibliographyOpen(!isBibliographyOpen)}
        >
          <div className={styles.collapsibleTitle}>
            Bibliography
            <span style={{ 
              fontSize: "12px", 
              fontWeight: "600", 
              color: "#667eea",
              backgroundColor: "#eef2ff",
              padding: "2px 8px",
              borderRadius: "6px",
              textTransform: "none",
              letterSpacing: "0",
            }}>
              {citationStyle}
            </span>
          </div>
          {isBibliographyOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
        </div>
        {isBibliographyOpen && (
          <div className={styles.collapsibleContent}>
            <Button
              appearance="primary"
              size="medium"
              icon={<BookSearch24Regular />}
              onClick={handleGenerateBibliography}
              disabled={isGenerating}
              style={{
                width: "100%",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              }}
            >
              {isGenerating ? "Generating..." : "Generate Bibliography"}
            </Button>
          </div>
        )}
      </div>

      <div>
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setIsReadingListOpen(!isReadingListOpen)}
        >
          <div className={styles.collapsibleTitle}>
            Reading List
            <span style={{ 
              fontSize: "12px", 
              fontWeight: "600", 
              color: "#667eea",
              backgroundColor: "#eef2ff",
              padding: "2px 8px",
              borderRadius: "6px",
              textTransform: "none",
              letterSpacing: "0",
            }}>
              {selectedReadingList === "all" ? "All Papers" : selectedReadingList}
            </span>
          </div>
          {isReadingListOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
        </div>
        {isReadingListOpen && (
          <div className={styles.collapsibleContent}>
            <div className={styles.dropdownWrapper}>
              <Dropdown
                appearance="outline"
                value={getReadingListDisplayLabel(selectedReadingList)}
                selectedOptions={[selectedReadingList]}
                onOptionSelect={(_, data) => handleReadingListChange(data.optionValue as string)}
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  border: "2px solid #e3e8ef",
                }}
              >
                <Option value="all">All Papers</Option>
                {readingLists.map((list) => (
                  <Option key={list} value={list}>
                    {list}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
        )}
      </div>

      <div>
        <div 
          className={styles.collapsibleHeader}
          onClick={() => setIsPapersOpen(!isPapersOpen)}
        >
          <div className={styles.collapsibleTitle}>
            Papers
            <span style={{ 
              fontSize: "12px", 
              fontWeight: "600", 
              color: "#667eea",
              backgroundColor: "#eef2ff",
              padding: "2px 8px",
              borderRadius: "6px",
              textTransform: "none",
              letterSpacing: "0",
            }}>
              {filteredPapers.length} {filteredPapers.length === 1 ? "paper" : "papers"}
            </span>
          </div>
          {isPapersOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
        </div>
        {isPapersOpen && (
          <div className={styles.collapsibleContent}>
            <div>
              <div 
                className={styles.collapsibleHeader}
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                style={{ 
                  padding: "10px 0",
                  margin: "0",
                  border: "none",
                  borderBottom: isFiltersOpen ? "1px solid #e3e8ef" : "none",
                }}
              >
                <div className={styles.collapsibleTitle} style={{ fontSize: "12px" }}>
                  🔍 Filters
                  {(searchQuery || sortOption !== "relevance") && (
                    <span style={{ 
                      fontSize: "10px", 
                      fontWeight: "600", 
                      color: "#667eea",
                      backgroundColor: "#eef2ff",
                      padding: "2px 6px",
                      borderRadius: "6px",
                      textTransform: "none",
                      letterSpacing: "0",
                    }}>
                      Active
                    </span>
                  )}
                </div>
                {isFiltersOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
              </div>
              {isFiltersOpen && (
                <div style={{ padding: "12px 0 16px 0" }}>
                  <div className={styles.searchContainer}>
                    <div className={styles.searchInputWrapper}>
                      <Input
                        appearance="outline"
                        placeholder="Search by title, author, or keyword..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        contentBefore={<span style={{ fontSize: "18px" }}>🔍</span>}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          border: "2px solid #e3e8ef",
                          transition: "all 0.2s ease",
                        }}
                      />
                    </div>
                    <div style={{ marginTop: "12px" }}>
                      <Dropdown
                        appearance="outline"
                        value={getSortDisplayLabel(sortOption)}
                        selectedOptions={[sortOption]}
                        onOptionSelect={(_, data) => setSortOption(data.optionValue as string)}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          border: "2px solid #e3e8ef",
                        }}
                      >
                        <Option value="relevance">Relevance</Option>
                        <Option value="starred">Starred Papers</Option>
                        <Option value="date-accessed-newest">Date Accessed (Newest First)</Option>
                        <Option value="date-accessed-oldest">Date Accessed (Oldest First)</Option>
                        <Option value="date-published-newest">Date Published (Newest First)</Option>
                        <Option value="date-published-oldest">Date Published (Oldest First)</Option>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="large" label="Loading papers..." />
              </div>
            ) : (
              <>
                <div style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 100,
                  padding: "16px",
                  background: "#ffffff",
                  borderBottom: "2px solid #e3e8ef",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}>
                  <div style={{ 
                    fontSize: "13px", 
                    color: "#4a5568",
                    fontWeight: "600",
                  }}>
                    {selectedPaperIds.size > 0 ? (
                      <span style={{ color: "#667eea" }}>
                        {selectedPaperIds.size} selected
                      </span>
                    ) : (
                      <span>Select papers to cite</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {selectedPaperIds.size > 0 && (
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={handleClearSelection}
                        style={{
                          borderRadius: "6px",
                          fontSize: "12px",
                        }}
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={handleInsertMultipleCitations}
                      disabled={selectedPaperIds.size === 0}
                      style={{
                        borderRadius: "6px",
                        background: selectedPaperIds.size === 0 
                          ? "#cbd5e0" 
                          : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "12px",
                        boxShadow: selectedPaperIds.size > 0 ? "0 2px 8px rgba(102, 126, 234, 0.3)" : "none",
                      }}
                    >
                      Insert Citation{selectedPaperIds.size > 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
                <PaperList 
                  papers={filteredPapers} 
                  selectedPaperIds={selectedPaperIds}
                  onToggleSelection={handleTogglePaperSelection}
                />
              </>
            )}
          </div>
        )}
      </div>

      {inlineCitationActive && (
        <InlineCitationPanel
          searchText={inlineSearchText}
          papers={inlineFilteredPapers}
          selectedIndex={inlineSelectedIndex}
          onSelect={handleInlineCitationSelect}
          onClose={() => {
            setInlineCitationActive(false);
            setInlineSearchText("");
          }}
        />
      )}
      
      <DiagnosticsPanel
        isOpen={isDiagnosticsOpen}
        onClose={() => setIsDiagnosticsOpen(false)}
        papers={papers}
      />
    </div>
  );
};

export default App;
